import { FC } from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { InfoCircle, LayersIcon, UsersIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import { FormInput } from '../../../components/input/index';
import {
  DividendFrequency,
  DividendPayout,
  DividendPayoutType,
} from '../../../hooks/dividend/dividend.types';
import { DividendHooks } from '../../../hooks/index';
import { getTranslation } from '../../../utils/get-translation.util';
import { AddDividendFormData, stepOneSchema } from './add-dividend.validation';
import { DividendSelectField } from './dividend-select-field';

type Props = {
  companyId: string;
  control: Control<AddDividendFormData>;
  editingPayoutId?: string;
  /** Only passed when editing: step one is the flow's first step, so back means the summary. */
  onBack?: () => void;
  onCancel: () => void;
  onEditDuplicate: (payout: DividendPayout) => void;
  onNext: () => void;
};

const CURRENT_YEAR = new Date().getFullYear();

const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2000 + 1 }, (_, i) => 2000 + i)
  .reverse()
  .map((year) => ({ label: String(year), value: year }));

const FREQUENCY_OPTIONS = [
  { label: getTranslation('DIVIDEND_FREQUENCY_ANNUALLY'), value: DividendFrequency.ANNUALLY },
  {
    label: getTranslation('DIVIDEND_FREQUENCY_SEMI_ANNUALLY'),
    value: DividendFrequency.SEMI_ANNUALLY,
  },
  { label: getTranslation('DIVIDEND_FREQUENCY_QUARTERLY'), value: DividendFrequency.QUARTERLY },
  { label: getTranslation('MONTHLY'), value: DividendFrequency.MONTHLY },
];

const PERIOD_INDEX_OPTIONS: Record<DividendFrequency, { label: string; value: number }[]> = {
  [DividendFrequency.ANNUALLY]: [],
  [DividendFrequency.MONTHLY]: Array.from({ length: 12 }, (_, i) => ({
    label: new Date(0, i).toLocaleString('en-US', { month: 'long' }),
    value: i + 1,
  })),
  [DividendFrequency.QUARTERLY]: [
    { label: 'Q1', value: 1 },
    { label: 'Q2', value: 2 },
    { label: 'Q3', value: 3 },
    { label: 'Q4', value: 4 },
  ],
  [DividendFrequency.SEMI_ANNUALLY]: [
    { label: 'H1', value: 1 },
    { label: 'H2', value: 2 },
  ],
};

const PERIOD_LABEL: Record<DividendFrequency, string> = {
  [DividendFrequency.ANNUALLY]: getTranslation('DIVIDEND_PERIOD'),
  [DividendFrequency.MONTHLY]: getTranslation('DIVIDEND_MONTH'),
  [DividendFrequency.QUARTERLY]: getTranslation('DIVIDEND_QUARTER'),
  [DividendFrequency.SEMI_ANNUALLY]: getTranslation('DIVIDEND_HALF'),
};

const TYPE_CARDS = [
  {
    description: getTranslation('DIVIDEND_TYPE_PER_SHARE_CLASS_DESCRIPTION'),
    icon: LayersIcon,
    label: getTranslation('PER_SHARE_CLASS'),
    value: DividendPayoutType.PER_SHARE_CLASS,
  },
  {
    description: getTranslation('DIVIDEND_TYPE_PER_STAKEHOLDER_DESCRIPTION'),
    icon: UsersIcon,
    label: getTranslation('PER_STAKEHOLDER'),
    value: DividendPayoutType.PER_STAKEHOLDER,
  },
];

const SectionLabel: FC<{ children: string }> = ({ children }) => (
  <p className="text-xs font-[450] text-gray-500">{children}</p>
);

export const AddDividendStepOne: FC<Props> = ({
  companyId,
  control,
  editingPayoutId,
  onBack,
  onCancel,
  onEditDuplicate,
  onNext,
}) => {
  const formValues = useWatch({ control });

  const { field: typeField } = useController({ control, name: 'type' });
  const { field: periodIndexField } = useController({ control, name: 'periodIndex' });

  const { name, type, year, frequency, periodIndex } = formValues;

  const isStepValid = stepOneSchema.safeParse({
    frequency,
    name,
    periodIndex,
    type,
    year,
  }).success;

  const showDetails = Boolean(type);
  const showPeriod = Boolean(frequency);
  const showPeriodIndex = frequency && frequency !== DividendFrequency.ANNUALLY;
  const periodOptions = frequency ? PERIOD_INDEX_OPTIONS[frequency] : [];

  const { records } = DividendHooks.useDividendPayouts({
    companyId,
    take: 100,
    year: year || undefined,
  });

  const isSamePeriod = (payout: (typeof records)[number]) =>
    Boolean(frequency) &&
    payout.frequency === frequency &&
    payout.year === year &&
    (payout.periodIndex ?? null) === (periodIndex ?? null);

  const duplicatePayout = records.find(
    (payout) => payout.id !== editingPayoutId && isSamePeriod(payout) && payout.type === type,
  );
  const hasFullDuplicate = Boolean(duplicatePayout);
  const hasPeriodDuplicate =
    !hasFullDuplicate &&
    records.some((payout) => payout.id !== editingPayoutId && isSamePeriod(payout));

  const trimmedName = name?.trim() ?? '';

  // Name must be unique across the whole company; search narrows to same-name payouts (any year).
  const { records: nameMatches } = DividendHooks.useDividendPayouts({
    companyId,
    search: trimmedName || undefined,
    take: 100,
  });

  const hasDuplicateName =
    Boolean(trimmedName) &&
    nameMatches.some(
      (payout) =>
        payout.id !== editingPayoutId &&
        payout.name.trim().toLowerCase() === trimmedName.toLowerCase(),
    );

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-4">
        <div className="flex flex-col gap-4">
          <SectionLabel>{getTranslation('TYPE')}</SectionLabel>
          <div className="flex gap-4">
            {TYPE_CARDS.map(({ label, value, description, icon: Icon }) => {
              const isSelected = typeField.value === value;
              return (
                <button
                  className={twMerge(
                    'flex flex-1 flex-col gap-3 rounded-lg border p-4 text-left transition-colors',
                    isSelected ? 'border-brand-50 bg-brand-25' : 'border-gray-100 bg-white',
                    editingPayoutId && 'cursor-not-allowed',
                    editingPayoutId && !isSelected && 'opacity-50',
                  )}
                  disabled={Boolean(editingPayoutId)}
                  key={value}
                  onClick={() => typeField.onChange(value)}
                  type="button"
                >
                  <Icon className="size-6" iconColor={isSelected ? '#2565C8' : '#667085'} />
                  <div className="flex flex-col">
                    <span
                      className={twMerge(
                        'text-sm',
                        isSelected ? 'font-[550] text-brand-700' : 'font-[450] text-gray-700',
                      )}
                    >
                      {label}
                    </span>
                    <span
                      className={twMerge(
                        'text-[10px] font-medium leading-4',
                        isSelected ? 'text-brand-600' : 'text-gray-400',
                      )}
                    >
                      {description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {showDetails && (
          <div className="flex flex-col gap-4">
            <SectionLabel>{getTranslation('PAYOUT')}</SectionLabel>
            <FormInput control={control} name="name" placeholder={getTranslation('NAME')} />
            {hasDuplicateName && (
              <div className="flex items-start gap-2 rounded-lg bg-fireside-25 p-3">
                <InfoCircle className="mt-0.5 size-4 shrink-0" />
                <p className="text-xs font-[450] text-gray-600">
                  {getTranslation('DIVIDEND_DUPLICATE_NAME_WARNING')}
                </p>
              </div>
            )}
          </div>
        )}

        {showDetails && (
          <div className="flex flex-col gap-4">
            <SectionLabel>{getTranslation('PERIOD')}</SectionLabel>
            <DividendSelectField
              control={control}
              name="frequency"
              onSelect={(value) =>
                periodIndexField.onChange(value === DividendFrequency.ANNUALLY ? null : 1)
              }
              options={FREQUENCY_OPTIONS}
              placeholder={getTranslation('FREQUENCY')}
            />

            {showPeriod && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <DividendSelectField
                    control={control}
                    name="year"
                    options={YEAR_OPTIONS}
                    placeholder={getTranslation('YEAR')}
                  />
                </div>
                {showPeriodIndex && (
                  <div className="flex-1">
                    <DividendSelectField
                      control={control}
                      name="periodIndex"
                      options={periodOptions}
                      placeholder={frequency ? PERIOD_LABEL[frequency] : getTranslation('PERIOD')}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showPeriod && hasFullDuplicate && (
          <div className="flex items-start gap-2 rounded-lg bg-fireside-25 p-3">
            <InfoCircle className="mt-0.5 size-4 shrink-0" />
            <p className="text-xs font-[450] text-gray-600">
              {getTranslation('DIVIDEND_DUPLICATE_FULL_WARNING')}{' '}
              {getTranslation('DIVIDEND_DUPLICATE_EDIT_HINT')}{' '}
              <button
                className="font-[550] text-brand-700 underline"
                onClick={() => duplicatePayout && onEditDuplicate(duplicatePayout)}
                type="button"
              >
                {getTranslation('CLICK_HERE').toLowerCase()}
              </button>
              .
            </p>
          </div>
        )}

        {showPeriod && hasPeriodDuplicate && (
          <div className="flex items-start gap-2 rounded-lg bg-gray-50 p-3">
            <InfoCircle className="mt-0.5 size-4 shrink-0" iconColor="#667085" />
            <p className="text-xs font-[450] text-gray-600">
              {getTranslation('DIVIDEND_DUPLICATE_PERIOD_WARNING')}
            </p>
          </div>
        )}
      </div>

      <div className="flex w-full shrink-0 items-center justify-between px-6">
        <Button
          className="h-9 w-fit bg-transparent px-3 py-1.5 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          {getTranslation('CANCEL')}
        </Button>
        <div className="flex items-center gap-3">
          {onBack && (
            <Button
              className="h-9 w-fit rounded border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
              onClick={onBack}
              styleType="NONE"
              type="button"
            >
              {getTranslation('BACK')}
            </Button>
          )}
          <Button
            className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white disabled:opacity-50"
            disabled={!isStepValid || hasFullDuplicate || hasDuplicateName}
            onClick={onNext}
            type="button"
          >
            {getTranslation('NEXT')}
          </Button>
        </div>
      </div>
    </>
  );
};
